import * as React from 'react';
import { atom, useAtomValue } from 'jotai';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';

import { useTaxonomyService } from '@restworld/data-services';
import { CheckboxGroup, FormLabelWrapper, Heading, HelperTextRElement, TextInput } from '@restworld/ui-ds-base';
import { Occupation, Variable, Worker } from '@restworld/utility-types';

import {
  EditModalFooter,
  EditModalTitle,
  EditFieldOptionsModal,
  IEditModalProps,
  FieldOptionsViewType,
} from '../profile-edit-modal';
import { getWorkerAtom, getWorkerFieldOptionsAtom } from '../../../global-state.worker-data';
import { useApiSubmitHandler } from '../../hooks';

type IPreferencesFormContentProps = IEditModalProps;
interface InputProps {
  domain_interest: string;
  occupations: string[];
  restaurant_types: string[];
  shifts: string[];
  job_types: string[];
}
const defaultValuesAtom = atom((get) => {
  const { data: workerData } = get(getWorkerAtom);

  return {
    domain_interest: workerData?.research_info?.domain_interest ?? '',
    occupations:
      workerData?.research_info?.occupations
        ?.filter(Boolean)
        .map((v) => v?.id?.toString())
        .filter(Boolean) ?? [],
    restaurant_types:
      workerData?.research_info?.restaurant_types?.filter(Boolean).map((v) => v?.restaurant_type_var.id) ?? [],
    shifts: workerData?.shifts?.filter(Boolean).map((v) => v?.shift_var.id) ?? [],
    job_types: workerData?.job_types?.filter(Boolean)?.map((v) => v?.job_type_var.id) ?? [],
  };
});
// -- main -- //
export const PreferencesFormContent: React.FunctionComponent<IPreferencesFormContentProps> = ({
  editPageTitle,
  onClose,
}) => {
  const { t } = useTranslation();
  const { fieldOptionsView, toggleFieldOptionsView } = useFieldOptions();
  const defaultValues = useAtomValue(defaultValuesAtom);
  const { data: workerFieldOptionsData } = useAtomValue(getWorkerFieldOptionsAtom);
  const { data: workerData } = useAtomValue(getWorkerAtom);
  const {
    handleSubmit,
    formState: { errors },
    control,
    getValues,
    setValue,
    reset,
  } = useForm<InputProps>({ reValidateMode: 'onSubmit', defaultValues });
  const { apiSubmitHandler, loading } = useApiSubmitHandler({ onClose, editPageType: 'preferences' });
  const domainInterestId = getValues('domain_interest');
  const { occupationOptions } = useStepOptions({
    domainInterestId,
    domainInterest: workerFieldOptionsData?.domainInterest,
  });
  const onSubmit: SubmitHandler<InputProps> = React.useCallback(
    async (data) => {
      try {
        const workerPreferences = workerData?.research_info;
        const params = {
          research_info: {
            ...workerPreferences,
            domain_interest: data.domain_interest,
            ...(data.occupations
              ? {
                  occupations: data.occupations.map((v) => ({
                    id: +v,
                  })),
                }
              : {}),
            restaurant_types: data.restaurant_types.map((v) => ({
              restaurant_type_var: { id: v },
            })),
          } as Worker['research_info'],
          shifts: data.shifts.map((v) => ({ shift_var: { id: v } })) as Worker['shifts'],
          job_types: data.job_types.map((v) => ({ job_type_var: { id: v } })) as Worker['job_types'],
        };
        apiSubmitHandler(params);
      } catch (error) {
        console.error(error, 'error submitting preferences');
      }
    },
    [apiSubmitHandler, workerData?.research_info]
  );
  const handleChange = React.useCallback(
    (value: string | React.ChangeEvent<HTMLInputElement>) => {
      if (typeof value === 'string') {
        toggleFieldOptionsView((p) => ({ ...p, selected: value }));
      } else {
        const values = fieldOptionsView.selected ?? [];
        let checkV: string[] = values ? [...values] : [];
        checkV =
          !checkV.includes(value.target.id) && value.target.checked
            ? [...checkV, value.target.id]
            : checkV.filter((v) => v !== value.target.id);
        toggleFieldOptionsView((p) => ({ ...p, selected: checkV }));
      }
    },
    [fieldOptionsView.selected, toggleFieldOptionsView]
  );

  const handleSave = React.useCallback(() => {
    setValue(fieldOptionsView.id, fieldOptionsView.selected);
    toggleFieldOptionsView(defaultModalFieldOptions);
  }, [fieldOptionsView.id, fieldOptionsView.selected, setValue, toggleFieldOptionsView]);

  return (
    <>
      <div className="w-full">
        <EditModalTitle editPageTitle={editPageTitle} onClose={onClose} />
        <Heading size={5} color="muted-foreground" className="pb-6 lg:max-w-[446px]">
          {t('onboarding-page:enter_your_preferences_regarding_your_next_job')}
        </Heading>
        <form autoComplete="off" method="post" onSubmit={handleSubmit(onSubmit)} className="flex gap-4 flex-col">
          <div className="flex flex-col gap-4 md:flex-row">
            <FormLabelWrapper isFullWidth id="domain_interest" label={t('common:sector')}>
              <Controller
                name="domain_interest"
                control={control}
                rules={{ required: false }}
                render={({ field }) => (
                  <TextInput
                    id="domain_interest"
                    placeholder={t('onboarding-page:choose_sector')}
                    type="text"
                    rightIcon={ChevronDownIcon}
                    helperText={
                      Object.keys(errors).includes('domain_interest') ? <HelperTextRElement helperText="" /> : null
                    }
                    color={Object.keys(errors).includes('domain_interest') ? 'failure' : undefined}
                    readOnly
                    {...field}
                    value={workerFieldOptionsData?.domainInterest?.find((v) => v.id === field.value)?.label || ''}
                    onClick={() => {
                      toggleFieldOptionsView({
                        showOptionsView: true,
                        id: 'domain_interest',
                        selected: field.value ?? '',
                        options: workerFieldOptionsData?.domainInterest,
                        mainTitle: t('common:sector'),
                        returnType: 'string',
                      });
                    }}
                  />
                )}
              />
            </FormLabelWrapper>
            <FormLabelWrapper
              isFullWidth
              disabled={!domainInterestId}
              id="restaurant_types"
              label={t('onboarding-page:type_of_venue')}
            >
              <Controller
                name="restaurant_types"
                control={control}
                rules={{ required: false }}
                render={({ field }) => (
                  <TextInput
                    id="restaurant_types"
                    placeholder={t('onboarding-page:choose_type')}
                    type="text"
                    rightIcon={ChevronDownIcon}
                    helperText={
                      Object.keys(errors).includes('restaurant_types') ? <HelperTextRElement helperText="" /> : null
                    }
                    color={Object.keys(errors).includes('restaurant_types') ? 'failure' : undefined}
                    readOnly
                    {...field}
                    value={
                      field.value
                        ?.map(
                          (v) => workerFieldOptionsData.restaurantType?.find((d: { id: string }) => d.id === v)?.label
                        )
                        .filter(Boolean)
                        .join(', ') || ''
                    }
                    onClick={() => {
                      toggleFieldOptionsView({
                        showOptionsView: true,
                        id: 'restaurant_types',
                        mainTitle: t('onboarding-page:type_of_venue'),
                        options: workerFieldOptionsData.restaurantType,
                        selected: field.value,
                        returnType: 'arrayString',
                      });
                    }}
                    disabled={!domainInterestId}
                  />
                )}
              />
            </FormLabelWrapper>
          </div>
          <FormLabelWrapper disabled={!domainInterestId} id="occupations" label={t('common:role')}>
            <Controller
              name="occupations"
              control={control}
              rules={{ required: false }}
              render={({ field }) => {
                return (
                  <TextInput
                    id="occupations"
                    placeholder={t('onboarding-page:enter_your_role')}
                    type="text"
                    rightIcon={ChevronDownIcon}
                    helperText={
                      Object.keys(errors).includes('occupations') ? <HelperTextRElement helperText="" /> : null
                    }
                    color={Object.keys(errors).includes('occupations') ? 'failure' : undefined}
                    readOnly
                    {...field}
                    value={
                      field.value
                        ?.map((v) => occupationOptions?.find((d: { id: string }) => d.id === v)?.label)
                        .filter(Boolean)
                        ?.join(', ') || ''
                    }
                    onClick={() => {
                      toggleFieldOptionsView({
                        showOptionsView: true,
                        id: 'occupations',
                        mainTitle: t('common:role'),
                        options: occupationOptions,
                        selected: field.value,
                        returnType: 'arrayString',
                      });
                    }}
                    disabled={!domainInterestId}
                  />
                );
              }}
            />
          </FormLabelWrapper>
          <FormLabelWrapper
            disabled={!domainInterestId}
            id="shifts"
            label={
              <>
                {t('common:working_hours')}{' '}
                <Heading size={5} color="muted-foreground" weight="normal" className="leading-5">
                  {t('onboarding-page:select_one_or_more_options')}
                </Heading>
              </>
            }
          >
            <Controller
              name="shifts"
              control={control}
              rules={{ required: false }}
              render={({ field }) => (
                <CheckboxGroup
                  key="shifts"
                  options={workerFieldOptionsData?.shifts ?? []}
                  mode="clickable"
                  variant="chip"
                  isWrapList={false}
                  disabled={!domainInterestId}
                  className={'!px-0 pt-4'}
                  {...{
                    ...field,
                    onChange: (e) => {
                      let checkV: string[] = field.value ? [...field.value] : [];
                      checkV =
                        !checkV.includes(e.target.id) && e.target.checked
                          ? [...checkV, e.target.id]
                          : checkV.filter((v) => v !== e.target.id);
                      field.onChange(checkV.length ? checkV : []);
                    },
                  }}
                />
              )}
            />
          </FormLabelWrapper>
          <FormLabelWrapper disabled={!domainInterestId} id="job_types" label={t('common:work_commitment')}>
            <Controller
              name="job_types"
              control={control}
              rules={{ required: false }}
              render={({ field }) => (
                <CheckboxGroup
                  key="job_types"
                  options={workerFieldOptionsData?.jobType}
                  mode="clickable"
                  variant="chip"
                  isWrapList={false}
                  disabled={!domainInterestId}
                  className={'!px-0 pt-4'}
                  {...{
                    ...field,
                    onChange: (e) => {
                      let checkV: string[] = field.value ? [...field.value] : [];
                      checkV =
                        !checkV.includes(e.target.id) && e.target.checked
                          ? [...checkV, e.target.id]
                          : checkV.filter((v) => v !== e.target.id);
                      field.onChange(checkV);
                    },
                    value: field.value,
                  }}
                />
              )}
            />
          </FormLabelWrapper>
          <EditModalFooter
            loading={loading}
            onClose={() => {
              reset(defaultValues);
              onClose();
            }}
            editPageType="preferences"
          />
        </form>
      </div>
      <EditFieldOptionsModal
        {...fieldOptionsView}
        mainTitle={fieldOptionsView.mainTitle}
        value={fieldOptionsView.selected ?? getValues(fieldOptionsView.id)}
        onChange={handleChange}
        onBack={() => {
          toggleFieldOptionsView(defaultModalFieldOptions);
        }}
        onSave={handleSave}
        visible={fieldOptionsView.showOptionsView}
      />
    </>
  );
};

export default PreferencesFormContent;

interface FieldOptionsModalViewType extends Omit<FieldOptionsViewType, 'id'> {
  id: keyof InputProps;
}
const defaultFieldOptions: FieldOptionsModalViewType = {
  showOptionsView: false,
  options: [],
  mainTitle: '',
  subTitle: '',
  id: 'domain_interest',
  returnType: 'string',
};
const defaultModalFieldOptions = {
  ...defaultFieldOptions,
  id: 'domain_interest',
} as FieldOptionsModalViewType;
const useFieldOptions = () => {
  const [fieldOptionsView, toggleFieldOptionsView] = React.useState<FieldOptionsModalViewType | null>({
    ...defaultModalFieldOptions,
  });
  return {
    fieldOptionsView,
    toggleFieldOptionsView,
  };
};
const useStepOptions = ({
  domainInterestId,
  domainInterest,
}: {
  domainInterestId: string;
  domainInterest: Variable[];
}) => {
  const [occupationOptions, setOccupationOptions] = React.useState<Occupation[]>([]);
  const taxonomyService = useTaxonomyService();
  const occupationOptionsCp: Variable[] = React.useMemo(
    () =>
      occupationOptions
        .filter((v) => v.label_it)
        .map((v) => ({
          id: v.id?.toString() ?? '',
          key: v.id?.toString() ?? '',
          label: v.label_it.label,
          description: v.label_it.description,
        })),
    [occupationOptions]
  );
  const getOccupationOptions = React.useCallback(async () => {
    if (!domainInterestId) return;
    try {
      let domainInterestIdCp = '';
      const findIndex = domainInterest.findIndex((v) => v.id === domainInterestId);
      if (findIndex > -1) domainInterestIdCp = String(domainInterest[findIndex]['id']);
      const response = await taxonomyService.indexOccupations(domainInterestIdCp);
      setOccupationOptions(response.data);
    } catch (error) {
      console.error(`error fetching occupation`, error);
    }
  }, [domainInterest, domainInterestId, taxonomyService]);
  React.useEffect(() => {
    getOccupationOptions();
  }, [getOccupationOptions]);
  const returnV = React.useMemo(
    () => ({
      occupationOptions: occupationOptionsCp,
    }),
    [occupationOptionsCp]
  );
  return returnV;
};
